import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild, inject } from '@angular/core';
import { ActionSheetController, IonButton, IonIcon, ToastController } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CameraService, JpgToPdfService, PlatformService } from '@okcargo-apps/shared-infrastructure-ionic';

@Component({
  selector: 'lib-file-drop-area',
  templateUrl: './file-drop-area.component.html',
  styleUrls: ['./file-drop-area.component.scss'],
  imports: [IonIcon, IonButton, NgClass, TranslateModule],
})
export class FileDropAreaComponent implements OnDestroy {
  private readonly platform = inject(PlatformService);
  public readonly cameraService = inject(CameraService);
  private readonly jpgToPdfService = inject(JpgToPdfService);
  private readonly translate = inject(TranslateService);
  private readonly actionSheetCtrl = inject(ActionSheetController);
  private readonly toastCtrl = inject(ToastController);

  @Output()
  fileToParentComponent = new EventEmitter<File>();

  @Input()
  hideButton = false;

  @ViewChild('fileInputDocumentMobile', { static: false })
  fileInputDocumentMobile!: ElementRef;

  public selectedFile: File | null = null;
  public isDragging = false;

  public isHybrid: boolean;

  constructor() {
    this.isHybrid = this.platform.isHybrid;
  }

  ngOnDestroy(): void {
    if (this.isHybrid) this.cameraService.clearPhoto();
  }

  public onFileSelected(event: any): void {
    this.selectedFile = event.target.files[0];
    if (this.selectedFile && this.hideButton) {
      this.fileToParentComponent.emit(this.selectedFile);
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent): void {
    this.isDragging = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer && event.dataTransfer.files.length) {
      this.selectedFile = event.dataTransfer.files[0];
    }
    if (this.selectedFile && this.hideButton) {
      this.emitFileToParentComponent();
    }
  }

  private async takePhotoInMobile(): Promise<void> {
    try {
      await this.cameraService.takePhoto();

      if (this.hideButton) this.emitFileToParentComponent();

      this.selectedFile = null; // clear file

      // TODO use type unknown or error or whatever
    } catch (error: any) {
      console.error('Error:', error);

      // TODO translate
      let errorMsg = '';

      if (error.message === 'PERMISSION_DENIED') {
        errorMsg = 'Necesitamos permisos de cámara para continuar.';
      } else if (error.message && error.message.includes('User cancelled')) {
        errorMsg = 'Has cancelado la toma de la foto.';
      } else {
        errorMsg = 'Error: ' + error.message;
      }

      errorMsg += '<br><strong>Por favor, contacte con soporte si necesita ayuda.</strong>';

      // TODO refactor: move to a service that centralizes how these types of notices are displayed in the UI
      const toast = await this.toastCtrl.create({
        message: errorMsg,
        duration: 10000,
        color: 'danger',
        position: 'bottom',
      });
      await toast.present();
    }
  }

  private async getPhotoFileinMobile(): Promise<void> {
    await this.cameraService.getPhotoFromGallery();
    if (this.hideButton) this.emitFileToParentComponent();
    this.selectedFile = null; // clear file
  }

  private getFileInMobile(): void {
    this.fileInputDocumentMobile.nativeElement.click();
    this.cameraService.clearPhoto();
  }

  private cleanFileAndPhoto(): void {
    this.selectedFile = null;
    this.cameraService.clearPhoto();
  }

  public async emitFileToParentComponent(): Promise<void> {
    if (this.selectedFile) {
      this.fileToParentComponent.emit(this.selectedFile);
      // this.selectedFile = null;
    } else if (this.cameraService.photo) {
      try {
        this.selectedFile = await this.jpgToPdfService.convertBase64ImageToPdf(this.cameraService.photo);
        if (this.selectedFile) {
          this.fileToParentComponent.emit(this.selectedFile);
        }

        // TODO use type unknown or error or whatever
      } catch (error: any) {
        console.error('Error:', error);

        // TODO translate
        let errorMsg = 'Error al convertir la imagen a PDF' + error;

        errorMsg += '<br><strong>Por favor, contacte con soporte si necesita ayuda.</strong>';

        // TODO refactor: move to a service that centralizes how these types of notices are displayed in the UI
        const toast = await this.toastCtrl.create({
          message: errorMsg,
          duration: 10000,
          color: 'danger',
          position: 'bottom',
        });
        await toast.present();
      }
    }
    if (!this.hideButton) this.cleanFileAndPhoto();
  }

  // TODO translate this
  public async viewActionSheet(): Promise<void> {
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Subir documento',
      buttons: [
        {
          icon: '../assets/material-symbols/photo_camera_24dp_FILL0_wght400_GRAD0_opsz24.svg',
          text: 'Hacer foto del documento', // this.translate.instant('files.ecmr'),
          data: {
            action: 'takePhotoInMobile',
          },
          handler: async () => {
            await actionSheet.dismiss();
            this.takePhotoInMobile();
          },
        },
        {
          icon: '../assets/material-symbols/photo_library_24dp_FILL0_wght400_GRAD0_opsz24.svg',
          text: 'Foto de la galería', // this.translate.instant('files.loading_order'),
          data: {
            action: 'getPhotoFileinMobile',
          },
          handler: async () => {
            await actionSheet.dismiss();
            this.getPhotoFileinMobile();
          },
        },
        {
          icon: '../assets/material-symbols/upload_file_24dp_FILL0_wght400_GRAD0_opsz24.svg',
          text: 'Seleccionar documento PDF', // this.translate.instant('files.loading_order'),
          data: {
            action: 'getFileInMobile',
          },
          handler: () => {
            this.getFileInMobile();
          },
        },
        {
          text: this.translate.instant('files.cancel'),
          role: 'cancel',
        },
      ],
    });

    await actionSheet.present();
  }
}
